// The top bar's user menu (App admin's): name, email and role; My account (password, 2-step); Sign out. Signing
// out asks first while the workflow canvas has unsaved work (lib/unsavedWork.ts), so it is never lost silently.
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, KeyRound, LogOut } from 'lucide-react'
import { ConfirmDialog, useOutsideClick } from '@the-kit/ui'
import { ROLE_LABEL } from '@/constants/roles'
import { ROUTES } from '@/constants/routes'
import { useUnsavedWork } from '@/lib/unsavedWork'
import { useMe } from '../session'
import { useSignOut } from '../useSession'

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

export function UserMenu() {
  const { user } = useMe()
  const navigate = useNavigate()
  const signOut = useSignOut()
  const unsaved = useUnsavedWork((state) => state.unsaved)
  const [open, setOpen] = useState(false)
  const [asking, setAsking] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useOutsideClick(ref, () => setOpen(false), open)
  const leave = () => signOut.mutate(undefined)
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="flex items-center gap-2 rounded px-1 py-1 hover:bg-slate-100 dark:hover:bg-slate-700 sm:px-2"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="user-menu"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-dark text-2xs font-semibold text-white">
          {initialsOf(user.name)}
        </span>
        <span className="hidden text-xs font-medium text-default md:inline">{user.name.split(/\s+/)[0]}</span>
        <ChevronDown size={12} className={`hidden text-slate-400 md:inline ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 z-50 mt-1 w-60 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-surface-dark"
          role="menu"
        >
          <div className="border-b border-slate-100 bg-surface-2 px-3 py-2.5 dark:border-slate-700 dark:bg-surface-dark-2">
            <div className="text-xs font-semibold text-default">{user.name}</div>
            <div className="mt-0.5 text-2xs text-muted">
              {user.email} · {ROLE_LABEL[user.role]}
            </div>
          </div>
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-xs text-default hover:bg-slate-50 dark:hover:bg-slate-700"
            onClick={() => {
              setOpen(false)
              navigate(ROUTES.account.to())
            }}
            data-testid="menu-account"
          >
            <KeyRound size={14} className="text-muted" /> My account (password, 2-step)
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2 text-xs text-red-600 hover:bg-red-50 dark:border-slate-700 dark:text-red-400 dark:hover:bg-red-900/20"
            onClick={() => {
              setOpen(false)
              if (unsaved) setAsking(true)
              else leave()
            }}
            data-testid="sign-out"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
      <ConfirmDialog
        isOpen={asking}
        type="warning"
        title="Leave without saving?"
        message="The workflow canvas has changes that are not saved. They are lost when you sign out."
        confirmText="Sign out"
        onClose={() => setAsking(false)}
        onConfirm={() => {
          setAsking(false)
          leave()
        }}
      />
    </div>
  )
}
